import React, { useEffect, useRef, useState } from "react";
import Spacing from "../Spacing";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { can_visit, notify } from "../../helpers";
import { Check, ChevronLeft, ChevronRight, Loader, Plus, Send, X } from "lucide-react";
import { addInsuranceNpi, addNewNpiManagement, getNpiandInsurance, searchNewNPI } from "../../services/npiManagement";
import Select from "react-select";

export default function NewAddNPI(props) {
  const { account_id, location_id, manageInsurance = false } = props;
  const STEP = { PRACTICE: 1, INSURANCE: 2 };

  // --- defaults ---
  const defaultGroupValues = [{ npi_number: "", organization_name: "" }];
  const defaultIndividualValues = [{ npi_number: "", first_name: "", last_name: "" }];
  const defaultInsuranceValues = {
    account_id: account_id,
    location_id: location_id,
    insurances: [],
  };

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState(defaultGroupValues);
  const [individuals, setIndividuals] = useState(defaultIndividualValues);
  const [insurances, setInsurances] = useState([]);
  const [formInsuranceData, setFormInsuranceData] = useState(defaultInsuranceValues);
  const [formResetKey, setFormResetKey] = useState(0);
  const [activeStep, setActiveStep] = useState(STEP.PRACTICE);

  const [groupCheckedNpi, setGroupCheckedNpi] = useState({});
  const [groupNpiStatus, setGroupNpiStatus] = useState({});
  const [groupNpiLoading, setGroupNpiLoading] = useState({});
  const groupNpiTimersRef = useRef({});

  const [individualCheckedNpi, setIndividualCheckedNpi] = useState({});
  const [individualNpiStatus, setIndividualNpiStatus] = useState({});
  const [individualNpiLoading, setIndividualNpiLoading] = useState({});
  const individualNpiTimersRef = useRef({});

  const MOBILE_BREAKPOINT = 700;

  useEffect(() => {
    if (manageInsurance) {
      setActiveStep(STEP.INSURANCE);
      goNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageInsurance]);

  const isNPI = (npiNumber) => {
    if (!/^\d{10}$/.test(npiNumber)) return null;
    let totalSum = 24;
    for (let i = 0; i < 9; i++) {
      let digit = parseInt(npiNumber[i], 10);
      if (i % 2 === 0) {
        digit *= 2;
        if (digit >= 10) {
          digit = digit.toString().split("").reduce((a, b) => a + parseInt(b, 10), 0);
        }
      }
      totalSum += digit;
    }
    const checkDigit = (10 - (totalSum % 10)) % 10;
    return checkDigit === parseInt(npiNumber[9], 10) ? parseInt(npiNumber, 10) : null;
  };

  const hasText = (v) => typeof v === "string" && v.trim() !== "";

  const hasAnyNpiInput = React.useMemo(() => {
    const groupHas = groups.some((g) => hasText(g.npi_number) || hasText(g.organization_name));
    const indivHas = individuals.some((p) => hasText(p.npi_number) || hasText(p.first_name) || hasText(p.last_name));
    return groupHas || indivHas;
  }, [groups, individuals]);

  const setGroupField = (index, field, value) => {
    setGroups((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const setGroupNpiStatusFor = (i, status) => setGroupNpiStatus((p) => ({ ...p, [i]: status }));
  const setGroupNpiLoadingFor = (i, flag) => setGroupNpiLoading((p) => ({ ...p, [i]: flag }));
  const setGroupCheckedFor = (i, flag) => setGroupCheckedNpi((p) => ({ ...p, [i]: flag }));

  const setIndividualField = (index, field, value) => {
    setIndividuals((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const setIndividualNpiStatusFor = (i, status) => setIndividualNpiStatus((p) => ({ ...p, [i]: status }));
  const setIndividualNpiLoadingFor = (i, flag) => setIndividualNpiLoading((p) => ({ ...p, [i]: flag }));
  const setIndividualCheckedFor = (i, flag) => setIndividualCheckedNpi((p) => ({ ...p, [i]: flag }));

  const handleGroupChange = (index, e) => {
    const { name, value } = e.target;
    if (name === "npi_number") {
      const clean = value.replace(/[^0-9]/g, "").slice(0, 10);
      setGroupField(index, "npi_number", clean);

      if (groupCheckedNpi[index]) {
        setGroupCheckedFor(index, false);
        setGroupField(index, "organization_name", "");
      }

      if (clean.length === 10) {
        const valid = isNPI(clean);
        setGroupNpiStatusFor(index, valid ? "valid" : "invalid");
        if (valid) {
          if (groupNpiTimersRef.current[index]) clearTimeout(groupNpiTimersRef.current[index]);
          groupNpiTimersRef.current[index] = setTimeout(() => {
            handleSearchNPI("group", index, clean);
          }, 400);
        }
      } else {
        setGroupNpiStatusFor(index, "idle");
        if (groupNpiTimersRef.current[index]) clearTimeout(groupNpiTimersRef.current[index]);
      }
      return;
    }
    setGroupField(index, name, value);
  };

  const addGroup = () => setGroups((prev) => [...prev, { npi_number: "", organization_name: "" }]);

  const removeGroup = (index) => {
    setGroups((prev) => prev.filter((_, i) => i !== index));
    if (groupNpiTimersRef.current[index]) clearTimeout(groupNpiTimersRef.current[index]);
    setGroupNpiStatus((prev) => { const { [index]: _, ...rest } = prev; return rest; });
    setGroupCheckedNpi((prev) => { const { [index]: _, ...rest } = prev; return rest; });
    setGroupNpiLoading((prev) => { const { [index]: _, ...rest } = prev; return rest; });
  };

  const handleIndividualChange = (index, e) => {
    const { name, value } = e.target;
    if (name === "npi_number") {
      const clean = value.replace(/[^0-9]/g, "").slice(0, 10);
      setIndividualField(index, "npi_number", clean);

      if (individualCheckedNpi[index]) {
        setIndividualCheckedFor(index, false);
        setIndividualField(index, "first_name", "");
        setIndividualField(index, "last_name", "");
      }

      if (clean.length === 10) {
        const valid = isNPI(clean);
        setIndividualNpiStatusFor(index, valid ? "valid" : "invalid");
        if (valid) {
          if (individualNpiTimersRef.current[index])
            clearTimeout(individualNpiTimersRef.current[index]);
          individualNpiTimersRef.current[index] = setTimeout(() => {
            handleSearchNPI("individual", index, clean);
          }, 400);
        }
      } else {
        setIndividualNpiStatusFor(index, "idle");
        if (individualNpiTimersRef.current[index])
          clearTimeout(individualNpiTimersRef.current[index]);
      }
      return;
    }
    setIndividualField(index, name, value);
  };

  const addIndividual = () => setIndividuals((prev) => [...prev, { npi_number: "", first_name: "", last_name: "" }]);

  const removeIndividual = (index) => {
    setIndividuals((prev) => prev.filter((_, i) => i !== index));
    if (individualNpiTimersRef.current[index]) clearTimeout(individualNpiTimersRef.current[index]);
    setIndividualNpiStatus((prev) => { const { [index]: _, ...rest } = prev; return rest; });
    setIndividualCheckedNpi((prev) => { const { [index]: _, ...rest } = prev; return rest; });
    setIndividualNpiLoading((prev) => { const { [index]: _, ...rest } = prev; return rest; });
  };

  const handleSearchNPI = async (kind, index, npi_number) => {
    if (!can_visit("practice_npi_management")) return;

    if (kind === "group") setGroupNpiLoadingFor(index, true);
    else setIndividualNpiLoadingFor(index, true);

    try {
      const response = await searchNewNPI({ npi_number });
      if (!response) return;

      if (response?.detail) {
        notify("error", response.detail);
        return;
      }

      if (response?.data?.status === "success") {
        const info = response?.data?.npi_info || {};
        if (kind === "group") {
          setGroupField(index, "organization_name", info.organization_name || "");
          setGroupCheckedFor(index, true);
        } else {
          setIndividualField(index, "first_name", info.first_name || "");
          setIndividualField(index, "last_name", info.last_name || "");
          setIndividualCheckedFor(index, true);
        }
      } else {
        notify("error", response?.data?.message ?? "Failed to fetch NPI info", 5);
      }
    } catch (error) {
      const msg = error?.response?.data?.detail;
      if (msg) notify("error", msg);
    } finally {
      if (kind === "group") setGroupNpiLoadingFor(index, false);
      else setIndividualNpiLoadingFor(index, false);
    }
  };

  const saveNpiIfNeeded = async () => {
    if (!hasAnyNpiInput) return true;
    try {
      setLoading(true);
      const npis = [
        ...(groups || [])
          .filter((g) => g.npi_number)
          .map((g) => ({ npi_type: "organization", npi_number: g.npi_number, organization_name: g.organization_name || "" })),
        ...(individuals || [])
          .filter((p) => p.npi_number)
          .map((p) => ({ npi_type: "individual", npi_number: p.npi_number, first_name: p.first_name || "", last_name: p.last_name || "" })),
      ];

      const newPayload = {
        account_id: account_id,
        location_id: location_id,
        npis,
      };

      const response = await addNewNpiManagement(newPayload);

      if (response?.detail) {
        notify("error", response.detail, 5);
        return false;
      }
      if (response?.data?.status === "success") {
        notify("success", response?.data?.message, 5);
        return true;
      }
      notify("error", response?.data?.message ?? "Something went wrong", 5);
      return false;
    } catch (error) {
      const msg = error?.response?.data?.detail;
      if (msg) notify("error", msg, 5);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchInsuranceAndNpiMapping = async () => {
    setLoading(true);
    try {
      const newPayload = {
        account_id: account_id,
        location_id: location_id,
      };

      const response = await getNpiandInsurance(newPayload);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
          return false;
        } else {
          if(response?.data?.status === "success"){
            setInsurances(response?.data?.insurances || []);

            const pnpis = Array.isArray(response?.data?.practice_npis) ? response?.data.practice_npis : [];
            const nextGroups = pnpis
              .filter((r) => r?.npi_type === "organization")
              .map((r) => ({
                npi_number: String(r?.npi_number ?? "").trim(),
                organization_name: r?.organization_name ?? "",
              }));
            const nextIndividuals = pnpis
              .filter((r) => r?.npi_type === "individual")
              .map((r) => ({
                npi_number: String(r?.npi_number ?? "").trim(),
                first_name: r?.first_name ?? "",
                last_name: r?.last_name ?? "",
              }));

            setGroups(nextGroups.length ? nextGroups : [{ npi_number: "", organization_name: "" }]);
            setIndividuals(nextIndividuals.length ? nextIndividuals : [{ npi_number: "", first_name: "", last_name: "" }]);

            const mapping = Array.isArray(response?.data?.npi_mapping) ? response?.data.npi_mapping : [];
            const preselected = mapping.map((m) => ({
              insurance_id: m?.insurance_id,
              npi_number: String(m?.npi_number ?? "").trim(),
            }));
            setFormInsuranceData((prev) => ({
              ...prev,
              insurances: preselected,
            }));

            return true;
          } else {
            notify("error", response?.data?.message, 5);
            return false;
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 5);
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const combinedNpiOptions = React.useMemo(() => {
    const group = (groups || [])
      .filter(g => (g?.npi_number || "").trim())
      .map(g => ({
        value: g.npi_number,
        label: `Group: ${g.npi_number} ${g.organization_name?.trim() && `(${g.organization_name})`}`,
      }));

    const indiv = (individuals || [])
      .filter(p => (p?.npi_number || "").trim())
      .map(p => ({
        value: p.npi_number,
        label: `Individual: ${p.npi_number} ${((p.first_name || "").trim() || (p.last_name || "").trim()) && `(${[(p.first_name || "").trim(), (p.last_name || "").trim()].filter(Boolean).join(" ")})`}`,
      }));

    return [...group, ...indiv];
  }, [groups, individuals]);

  const getInsuranceRow = (insurance_id) => {
    const found = (formInsuranceData.insurances || []).find(r => r.insurance_id === insurance_id);
    return found || null;
  };

  const upsertInsuranceRow = (row) => {
    setFormInsuranceData(prev => {
      const insList = [...(prev.insurances || [])];
      const idx = insList.findIndex(x => x.insurance_id === row.insurance_id);
      if (idx >= 0) insList[idx] = row;
      else insList.push(row);
      return { ...prev, insurances: insList };
    });
  };

  const handleInsuranceCombinedChange = (insurance_id, selected) => {
    if (!selected) {
      upsertInsuranceRow({
        insurance_id,
        npi_number: "",
      });
    } else {
      upsertInsuranceRow({
        insurance_id,
        npi_number: selected.value,
      });
    }
  };

  const goNext = async () => {
    const ok = await saveNpiIfNeeded();
    if (!ok) return;

    setActiveStep((s) => Math.min(STEP.INSURANCE, s + 1));
    await fetchInsuranceAndNpiMapping();

    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setActiveStep((s) => Math.max(STEP.PRACTICE, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitter = e.nativeEvent?.submitter;
    if (submitter) submitter.disabled = true;

    try {
      const response = await addInsuranceNpi(formInsuranceData);

      if (response?.detail) {
        notify("error", response.detail, 5);
      } else if (response?.data?.status === "success") {
        notify("success", response?.data?.message, 5);
        setActiveStep(STEP.PRACTICE);
        setFormResetKey(k => k + 1);
      } else {
        notify("error", response?.data?.message ?? "Something went wrong", 5);
      }
    } catch (error) {
      const msg = error?.response?.data?.detail;
      if (msg) notify("error", msg, 5);
    } finally {
      setLoading(false);
      if (submitter) submitter.disabled = false;
    }
  };

  const StepDot = ({ number, label }) => {
    const isActive = activeStep === number;
    const isDone = activeStep > number;
    const [showLabel, setShowLabel] = useState(
      typeof window !== "undefined" ? window.innerWidth > MOBILE_BREAKPOINT : true
    );
    useEffect(() => {
      const onResize = () => setShowLabel(window.innerWidth > MOBILE_BREAKPOINT);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);

    const circleStyle = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      height: 28,
      width: 28,
      marginRight: 8,
      backgroundColor: isDone ? "var(--accent)" : isActive ? "transparent" : "var(--bg_soft)",
      border: isActive ? "2px solid var(--accent)" : "none",
      color: isDone ? "#fff" : isActive ? "var(--accent)" : "var(--muted)",
      fontWeight: 600,
    };
    const labelStyle = { color: isActive ? "var(--accent)" : "var(--muted)", fontWeight: 600 };

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={circleStyle}>{isDone ? <Check size={16} /> : number}</div>
        {showLabel && <span style={labelStyle}>{label}</span>}
      </div>
    );
  };

  const Stepper = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: "60%", margin: "0 auto" }}>
      <StepDot number={STEP.PRACTICE} label="NPI Information" />
      <div style={{ flex: 1, height: 1, margin: "0 16px", backgroundColor: "var(--border)" }} />
      <StepDot number={STEP.INSURANCE} label="Insurance Settings" />
    </div>
  );

  useDocumentTitle("NPI Management");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 ">NPI Management</h2>
      </div>

      <Spacing lg="15" sm="20" />
      <Stepper />
      <Spacing lg="25" sm="35" />

      <form key={formResetKey} onSubmit={handleSubmit} autoComplete="off" className="relative">

        {activeStep === STEP.PRACTICE && (
          <div className="custom-scroll custom_height_npi_modal">
            <h6 className="docpro_fs_18 docpro_sky_color docpro_mb_0">Group NPI</h6>
            <Spacing lg="15" sm="15" />
            {/* Groups (Organizations) */}
            {groups.map((group, index) => {
              const status = groupNpiStatus[index] ?? "idle";
              const loadingRow = groupNpiLoading[index] ?? false;

              return (
                <div key={`grp-${index}`}>
                  <div className="flex mb-2 pb-1">
                    <div className="flex" style={{ flexDirection: "column" }}>
                      {(groups.length > 1) && (
                        <div className="text-right">
                          <Spacing lg="5" sm="5" />
                          <button
                            type="button"
                            onClick={() => removeGroup(index)}
                            className="docpro_icon_btn docpro_red_color docpro_icon_btn_red mr-2"
                            style={{ borderRadius: "50%", height: "30px", width: "30px" }}
                          >
                            <X width={20} height={20} />
                            <span className="docpro_tip">Remove Group</span>
                          </button>
                        </div>
                      )}
                      {(index === groups.length - 1) && (
                        <div className="text-right">
                          <Spacing lg="5" sm="5" />
                          <button
                            type="button"
                            onClick={addGroup}
                            className="docpro_icon_btn docpro_fw_600 docpro_primary_color mr-2"
                            style={{ borderRadius: "50%", height: "30px", width: "30px" }}
                          >
                            <Plus width={20} height={20} />
                            <span className="docpro_tip">Add New Group</span>
                          </button>
                        </div>
                      )}
                    </div>

                    <div
                      className={`w-full p-2 rounded-md docpro_locations ${
                        index % 2 === 0 && "docpro_bg_light"
                      }`}
                    >
                      <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_2">
                        <div className="docpro_grid_item">
                          <label className="docpro_fw_500 docpro_primary_color">NPI Number</label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              name="npi_number"
                              value={group?.npi_number || ""}
                              className={`docpro_form_field size_lg ${
                                status === "invalid" ? "docpro_border_red" : ""
                              }`}
                              autoComplete="off"
                              minLength={10}
                              maxLength={10}
                              onChange={(e) => handleGroupChange(index, e)}
                            />
                            {status !== "idle" && (
                              <span
                                style={{
                                  position: "absolute",
                                  right: 10,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: 12,
                                  color: status === "valid" ? "green" : "red",
                                }}
                              >
                                {status === "valid" ? "Valid" : "Invalid"}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="docpro_grid_item">
                          <label className="docpro_fw_500 docpro_primary_color">
                            Organization Name
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              name="organization_name"
                              value={group?.organization_name || ""}
                              onChange={(e) => handleGroupChange(index, e)}
                              className="docpro_form_field size_lg docpro_ternary_color"
                              autoComplete="off"
                              readOnly
                            />
                            {loadingRow && (
                              <span
                                className="docpro_loading"
                                style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, height: "100%" }}
                              >
                                <Loader width={16} height={16} />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Individuals (Providers) */}
            <h6 className="docpro_fs_18 docpro_sky_color docpro_mb_0">Individual NPI</h6>
            <Spacing lg="15" sm="15" />
            {individuals.map((individual, index) => {
              const status = individualNpiStatus[index] ?? "idle";
              const loadingRow = individualNpiLoading[index] ?? false;

              return (
                <div key={`ind-${index}`}>
                  <div className="flex mb-2 pb-1">
                    <div className="flex" style={{ flexDirection: "column" }}>
                      {(individuals.length > 1) && (
                        <div className="text-right">
                          <Spacing lg="5" sm="5" />
                          <button
                            type="button"
                            onClick={() => removeIndividual(index)}
                            className="docpro_icon_btn docpro_red_color docpro_icon_btn_red mr-2"
                            style={{ borderRadius: "50%", height: "30px", width: "30px" }}
                          >
                            <X width={20} height={20} />
                            <span className="docpro_tip">Remove Individual</span>
                          </button>
                        </div>
                      )}
                      {(index === individuals.length - 1) && (
                        <div className="text-right">
                          <Spacing lg="5" sm="5" />
                          <button
                            type="button"
                            onClick={addIndividual}
                            className="docpro_icon_btn docpro_fw_600 docpro_primary_color mr-2"
                            style={{ borderRadius: "50%", height: "30px", width: "30px" }}
                          >
                            <Plus width={20} height={20} />
                            <span className="docpro_tip">Add New Individual</span>
                          </button>
                        </div>
                      )}
                    </div>

                    <div
                      className={`w-full p-2 rounded-md docpro_locations ${
                        index % 2 === 0 && "docpro_bg_light"
                      }`}
                    >
                      <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3">
                        <div className="docpro_grid_item">
                          <label className="docpro_fw_500 docpro_primary_color">NPI Number</label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              name="npi_number"
                              value={individual?.npi_number || ""}
                              className={`docpro_form_field size_lg ${
                                status === "invalid" ? "docpro_border_red" : ""
                              }`}
                              autoComplete="off"
                              minLength={10}
                              maxLength={10}
                              onChange={(e) => handleIndividualChange(index, e)}
                            />
                            {status !== "idle" && (
                              <span
                                style={{
                                  position: "absolute",
                                  right: 10,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: 12,
                                  color: status === "valid" ? "green" : "red",
                                }}
                              >
                                {status === "valid" ? "Valid" : "Invalid"}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="docpro_grid_item">
                          <label className="docpro_fw_500 docpro_primary_color">First Name</label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              name="first_name"
                              value={individual?.first_name || ""}
                              onChange={(e) => handleIndividualChange(index, e)}
                              className="docpro_form_field size_lg docpro_ternary_color"
                              autoComplete="off"
                              readOnly
                            />
                            {loadingRow && (
                              <span
                                className="docpro_loading"
                                style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, height: "100%" }}
                              >
                                <Loader width={16} height={16} />
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="docpro_grid_item">
                          <label className="docpro_fw_500 docpro_primary_color">Last Name</label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              name="last_name"
                              value={individual?.last_name || ""}
                              onChange={(e) => handleIndividualChange(index, e)}
                              className="docpro_form_field size_lg docpro_ternary_color"
                              autoComplete="off"
                              readOnly
                            />
                            {loadingRow && (
                              <span
                                className="docpro_loading"
                                style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, height: "100%" }}
                              >
                                <Loader width={16} height={16} />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeStep === STEP.INSURANCE && (
          <div className="custom-scroll custom_height_npi_modal">
            <h6 className="docpro_fs_18 docpro_sky_color docpro_mb_0">Insurance Settings</h6>
            <Spacing lg="20" sm="20" />
            {/* Insurance settings UI here */}
            {insurances?.map((ins) => {
              const row = getInsuranceRow(ins.id);
              const selectedOption = row ? combinedNpiOptions.find(o => o.value === row.npi_number) || null : null;

              return (
                <div key={ins.id} className="docpro_card_soft"
                  style={{ paddingBottom: "10px", marginBottom: "10px", borderBottom: "1px solid var(--border)" }}
                >
                  <div className="docpro_grid docpro_grid_gap_10 docpro_grid_col_2">
                    <div className="docpro_grid_item docpro_fw_600 docpro_primary_color">{ins.name}</div>
                    <div className="docpro_grid_item">
                      <Select
                        isClearable
                        name={`npi_select_${ins.id}`}
                        options={combinedNpiOptions}
                        placeholder="Select Group or Individual NPI..."
                        isSearchable
                        value={selectedOption}
                        onChange={(selected) => handleInsuranceCombinedChange(ins.id, selected)}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        filterOption={(option, rawInput) => {
                          const words = rawInput.toLowerCase().split(" ");
                          return words.every((word) => option.label.toLowerCase().includes(word));
                        }}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "36px",
                            borderColor: state.isFocused ? "var(--accent)" : "var(--border)",
                            borderRadius: "8px",
                            boxShadow: state.isFocused ? "0 0 0 1px var(--accent)" : "none",
                            ":hover": { borderColor: "var(--accent)" },
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: "-2px",
                            borderRadius: "0 0 3px 3px",
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 1050, padding: 0 }),
                        }}
                      />
                      {combinedNpiOptions.length === 0 && (
                        <small className="docpro_muted_color">
                          No Group or Individual NPIs added yet.
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Nav Buttons */}
        <Spacing lg="18" sm="25" />
        <div className="flex items-center justify-between">
          <div>
            <button
              type="button"
              onClick={goBack}
              disabled={activeStep === STEP.PRACTICE}
              className={`docpro_bordered_btn size_lg ${
                activeStep === STEP.PRACTICE ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <ChevronLeft height={24} width={24} style={{ marginLeft: "-6px" }} />
              Back
            </button>
          </div>

          <div className="flex items-center gap-2">
            {activeStep !== STEP.INSURANCE && (
              <button
                type="button"
                onClick={goNext}
                className="docpro_bordered_btn docpro_fw_600 size_lg"
              >
                Next
                <ChevronRight height={24} width={24} style={{ marginRight: "-6px" }} />
              </button>
            )}

            {activeStep === STEP.INSURANCE && (
              <button
                className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                style={{ pointerEvents: `${loading ? "none" : "initial"}` }}
                type="submit"
              >
                <Send height={24} width={20} />
                Save
                {loading && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="absolute top-0 left-0 w-full" style={{ height: "100%", zIndex: 3 }}>
            <div
              className="flex items-center loading"
              style={{
                width: "100%",
                height: "100%",
                justifyContent: "center",
                background: "rgb(255 255 255 / 63%)",
              }}
            >
              <span className="docpro_loading" style={{ position: "inherit" }}>
                <Loader width={46} height={46} style={{ color: "var(--accent)" }} />
              </span>
            </div>
          </div>
        )}
      </form>
    </>
  );
}
